import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, QrCode, Store, Truck } from "lucide-react";
import { api } from "../lib/api";
import { rupiah } from "../lib/format";
import { TrackingTimeline } from "../components/TrackingTimeline";

export default function OrderDetail() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  useEffect(() => { api.get(`/orders/${id}`).then((r) => setO(r.data)); }, [id]);

  if (!o) return <div className="py-24 flex justify-center text-emerald-600"><Loader2 className="animate-spin" size={32} /></div>;

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-stone-900">Detail Pesanan</h1>
          <p className="font-mono text-sm text-stone-400">AWB: {o.awb}</p>
          {o.marking_code && <p className="font-mono text-sm text-emerald-700 font-semibold" data-testid="order-marking">Kode Marking: {o.marking_code}</p>}
        </div>
        <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${o.status === "CANCELLED" ? "bg-red-600 text-white" : "bg-emerald-600 text-white"}`}>{o.status === "CANCELLED" ? "Dibatalkan" : (o.tracking.find((t) => t.done && o.status === t.key)?.label || o.status)}</span>
      </div>

      {o.status === "CANCELLED" && <div data-testid="order-cancelled-banner" className="mb-6 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">Pengiriman pesanan ini telah <b>dibatalkan</b>{o.cancel_reason ? `: ${o.cancel_reason}` : ""}.</div>}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl border border-stone-200 p-6">
            <h3 className="font-display font-semibold mb-5">Pelacakan Kiriman (China → Indonesia)</h3>
            <TrackingTimeline tracking={o.tracking} />
          </div>
          <div className="bg-white rounded-2xl border border-stone-200 p-6">
            <h3 className="font-display font-semibold mb-4">Barang Dipesan</h3>
            <div className="space-y-3">
              {o.items.map((it, i) => (
                <div key={i} className="flex gap-3 items-center">
                  {it.image ? <img src={it.image} alt="" className="w-14 h-14 rounded-lg object-cover" /> : <div className="w-14 h-14 rounded-lg bg-stone-100" />}
                  <div className="flex-1"><p className="text-sm font-medium text-stone-800 line-clamp-1">{it.title}</p><p className="text-xs text-stone-400">{it.qty} × {rupiah(it.unit_price_idr)}</p></div>
                  <p className="font-semibold text-stone-700 text-sm">{rupiah(it.line_total_idr)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {o.fulfillment === "kedai" && (
            <div className="bg-emerald-900 text-white rounded-2xl p-6 text-center" data-testid="pickup-pass">
              <p className="text-xs uppercase tracking-widest text-emerald-300 flex items-center justify-center gap-1"><Store size={14} />Kode Ambil di Kedai</p>
              <p className="font-mono text-3xl font-extrabold mt-2 tracking-widest">{o.pickup_code}</p>
              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${o.pickup_code}`} alt="QR" className="mx-auto mt-3 rounded-lg bg-white p-1.5" />
              <p className="text-xs text-emerald-200/70 mt-3">Tunjukkan kode/QR ini ke agen kedai saat mengambil barang.</p>
            </div>
          )}
          <div className="bg-white rounded-2xl border border-stone-200 p-6">
            <h3 className="font-display font-semibold mb-4">Rincian Biaya</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-stone-600"><span>Subtotal barang</span><b>{rupiah(o.subtotal_idr)}</b></div>
              <div className="flex justify-between text-stone-600"><span>Ongkir ({o.total_weight_kg} kg)</span><b>{rupiah(o.shipping_fee_idr)}</b></div>
              {o.handling_fee_idr > 0 && <div className="flex justify-between text-stone-600"><span>Biaya kedai</span><b>{rupiah(o.handling_fee_idr)}</b></div>}
              <div className="flex justify-between text-base pt-2 border-t border-stone-100"><span className="font-semibold">Total</span><b className="text-emerald-700 font-display">{rupiah(o.total_idr)}</b></div>
            </div>
            <div className="mt-4 pt-4 border-t border-stone-100 text-sm text-stone-500 space-y-1">
              <p className="flex items-center gap-1.5">{o.fulfillment === "kedai" ? <Store size={14} /> : <Truck size={14} />} {o.fulfillment === "kedai" ? "Ambil di kedai" : "Kirim ke alamat"}</p>
              <p>Pembayaran: {o.payment_method}</p>
              {o.address && <p>Alamat: {o.address}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
